/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Image, Pagination } from 'react-bootstrap';
import { APP_COLORS } from "../../../../common/Constants";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { useCubeNamespace } from "../../../../common/utils/universeUtils";

import CompanyCard from "./CompanyCard";
import ContactCard from "./ContactCard";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import SpinnerOverlay from "../../../../common/components/SpinnerOverlay";
import ExploreFlyoutCompanyProfile from "../flyout/ExploreFlyoutCompanyProfile";

import config from "../../../../config";
import Alert from "react-bootstrap/Alert";

import brainLightning from "../../../../common/assets/img/tbd_icon_ai-brain-lightning.png";

import './ExploreCards.scss';

const CardListContainer = styled.div `
  display: grid;
  grid-auto-rows: minmax(min-content, max-content);
  grid-gap: 10px;
  padding-right: 5px;
  padding-left: 1px;
  height: calc(100vh - 255px);
  transition: width 300ms ease-in-out;
  overflow-y: auto;
  overflow-x: auto;
  width: ${props => props.removeWidth ? 'calc(100vw - 355px)' : 'calc(100vw - 40px)'};
 `;

const CardsWrapper = styled.div `
  position: relative;
  height: calc(100vh - 255px);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
`;

export default function ExploreCards(props) {
    const {
        loading,
        mode,
        handleSearch,
        expanded
    } = props;

    const universeState = useSelector(state => state.universe);
    const universeQueryState = useSelector(state => state.universeQuery);

    const currentUniverse = universeState.currentUniverse;
    const exploreByContact = universeState.universes[currentUniverse]?.exploreByContact ?? false;
    const modeKey = exploreByContact ? 'CONTACT' : 'COMPANY';

    const currentResults = universeQueryState.results[currentUniverse] || {};
    const modeResults = currentResults[modeKey] || {};

    const rawList = modeResults.listData || [];
    const isSearchLoading = universeQueryState.searchLoading;
    const isCountLoading = universeQueryState.countLoading;
    const mapStatus = currentResults.mapStatus;
    const mapErrorMessage = currentResults.mapErrorMessage;

    const pages = Math.ceil(rawList.length / config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE);

    const [selectedList, setSelectedList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectCompanyId, setSelectCompanyId] = useState(null);

    const [openCompanyProfile, setOpenCompanyProfile] = useState(false);

    const namespace = useCubeNamespace();

    // const companyDataState = useSelector(state => state.company);
    // const contactDataState = useSelector(state => state.contact);
    // const list = mode === 'COMPANY' ?  companyDataState.companies : contactDataState.contacts;
    // const pages = Math.ceil(list.length / config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE);
    //
    // const [selectedList, setSelectedList] = useState([]);
    // const [currentPage, setCurrentPage] = useState(1);
    // const [selectCompanyId, setSelectCompanyId] = useState(null);
    //
    // const [openCompanyProfile, setOpenCompanyProfile] = useState(false);

    useEffect(() => {
        if (rawList.length > 0) {
            const indexStart = 0;
            const indexEnd = config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE - 1;
            setSelectedList(rawList.slice(indexStart, indexEnd + 1));
            setCurrentPage(1);
            return;
        }
        setSelectedList([]);
        setCurrentPage(1);
    }, [rawList]);

    const handlePrevPage = () => {
        if (currentPage === 1) return;
        const _prevPage = currentPage - 1;
        const indexStart = (_prevPage * config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE) - config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE;
        const indexEnd = (_prevPage * config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE) - 1;
        setSelectedList(rawList.slice(indexStart, indexEnd + 1));
        setCurrentPage(_prevPage);
    };

    const handleSelectPage = (page) => {
        const indexStart = (page * config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE) - config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE;
        const indexEnd = (page * config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE) - 1;
        setSelectedList(rawList.slice(indexStart, indexEnd + 1));
        setCurrentPage(page);
    };

    const handleNextPage = () => {
        if (currentPage === pages) return;
        const _nextPage = currentPage + 1;
        const indexStart = (_nextPage * config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE) - config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE;
        const indexEnd = (_nextPage * config.constants.EXPLORE_LIST_VIEW_ITEMS_PER_PAGE) - 1;
        setSelectedList(rawList.slice(indexStart, indexEnd + 1));
        setCurrentPage(_nextPage);
    };

    const selectedListItems = useMemo(() => {
        return selectedList.map(l => ({
            companyId: l[`${namespace}.company_id`],
            companyIdentity: l[`${namespace}.company_identity`],
            companyScore: l[`${namespace}.company_score`],
            companyName: l[`${namespace}.company_name`],
            companyURL: l[`${namespace}.company_url`],
            companyURLStatus: l[`${namespace}.company_url_status`],
            companyAddress: `${l[`${namespace}.company_address_delivery_line_1`] || ''} ${l[`${namespace}.company_address_city`] || ''}, ${l[`${namespace}.company_address_region`] || ''} ${l[`${namespace}.company_address_postal_code`] || ''}`.trim(),
            companyPhone: l[`${namespace}.company_phone`],
            companyEmail: l[`${namespace}.company_email`],
            companyEmailStatus: l[`${namespace}.company_email_status`],
            companyClassTrades: l[`${namespace}.company_class_trades`],
            companyClassNAICSDescriptions: l[`${namespace}.company_class_naics_titles_formatted`],
            companyIndInsured: l[`${namespace}.company_ind_insured`],
            companyProfileCnt: l[`${namespace}.company_profile_cnt`],
            companyProfileManufacturerCnt: l[`${namespace}.company_profile_manufacturer_profile_cnt`],
            companyProfileAssociationCnt: l[`${namespace}.company_profile_association_profile_cnt`],
            companyLicenseCnt: l[`${namespace}.company_license_cnt`],
            companyPolicyCnt: l[`${namespace}.company_policy_cnt`],
            companyPermitCnt: l[`${namespace}.company_permit_cnt`],
            companyRegLocalRegistrationStatus: l[`${namespace}.company_registration_local_registration_status_mapped`],
            companyStatContactCnt: l[`${namespace}.company_contact_cnt`],
            companySourceCnt: l[`${namespace}.company_source_cnt`],

            // ข้อมูลฝั่ง Contact
            contactId: l[`${namespace}.contact_id`],
            contactIdentity: l[`${namespace}.contact_identity`],
            contactScore: l[`${namespace}.contact_score`],
            contactName: `${l[`${namespace}.contact_name_given_name`] || ''} ${l[`${namespace}.contact_name_surname`] || ''}`.trim(),
            contactTitle: l[`${namespace}.contact_title`],
            contactPhone: l[`${namespace}.contact_phone`],
            contactEmail: l[`${namespace}.contact_email`],
            contactEmailStatus: l[`${namespace}.contact_email_status`],
            contactRoles: l[`${namespace}.contact_roles`]
        }));
    }, [selectedList, currentUniverse, exploreByContact]);

    const selectCompany = (id) => {
        setSelectCompanyId(id);
        setOpenCompanyProfile(true);
    }

    return (
        <>
            {mapStatus === 'error' && mapErrorMessage && (
                <div>
                    <Alert
                        variant={"danger"}
                        dismissible={false}
                        show={true}
                        transition={true}
                    >
                        {mapErrorMessage}
                    </Alert>
                </div>
            )}

            {rawList.length > 0 && (
                <Row className="mt-3">
                    <Col xl={12} lg={12} md={12} sm={12}>
                        <CardsWrapper>
                            {/* ใช้สถานะ Loading จากตัวใหม่ */}
                            {(loading || isSearchLoading || isCountLoading) && (
                                <SpinnerOverlay customHeight={'100%'} customWidth={'100%'} />
                            )}

                            <CardListContainer removeWidth={expanded}>
                                {openCompanyProfile && (
                                    <div className='explore-company-profile'>
                                        <div className='close-box' onClick={(e) => setOpenCompanyProfile(false)}>
                                            <FontAwesomeIcon
                                                color={APP_COLORS['tech-blue']}
                                                icon={faTimes}
                                                style={{ marginLeft: 5, marginTop: 7 }}
                                            />
                                        </div>
                                        <div>
                                            <ExploreFlyoutCompanyProfile companyId={selectCompanyId} />
                                        </div>
                                    </div>
                                )}

                                {selectedListItems.map((l, i) => {
                                    const currentMode = exploreByContact ? 'CONTACT' : 'COMPANY';

                                    if (!exploreByContact) {
                                        return (
                                            <CompanyCard
                                                key={i}
                                                mode={currentMode}
                                                item={l}
                                                onSelectCompany={() => selectCompany(l.companyId)}
                                            />
                                        );
                                    } else {
                                        return (
                                            <ContactCard
                                                key={i}
                                                mode={currentMode}
                                                item={l}
                                                onSelectCompany={() => selectCompany(l.companyId)}
                                            />
                                        );
                                    }
                                })}

                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    {renderPagination({
                                        pages,
                                        currentPage,
                                        handlePrevPage,
                                        handleSelectPage,
                                        handleNextPage,
                                    })}
                                </div>
                            </CardListContainer>
                        </CardsWrapper>
                    </Col>
                </Row>
            )}

            {rawList.length === 0 && (
                <Row className="mt-3">
                    <Col xl={12} lg={12} md={12} sm={12} className="text-center text-muted">
                        <CardsWrapper>
                            {(loading || isSearchLoading || isCountLoading) && (
                                <SpinnerOverlay customHeight={'100%'} customWidth={'100%'} />
                            )}
                            <Image
                                src={brainLightning}
                                rounded={true}
                                width={'300px'}
                            /><br />
                            <p className="display-5 home font-weight-bold">Looks like you haven't selected anything.</p>
                            <p className="display-5 home font-weight-light">Add some filters and hit Apply</p>
                            <p>
                                <Button
                                    className="btn-min-width-120 btn-height-35"
                                    variant="info"
                                    onClick={() => handleSearch()}
                                >
                                    APPLY FILTER
                                </Button>
                            </p>
                        </CardsWrapper>
                    </Col>
                </Row>
            )}
        </>
    )
}


const renderPagination = (props) => {
    const {
        pages,
        currentPage,
        handlePrevPage,
        handleSelectPage,
        handleNextPage
    } = props;

    const items = [];
    for (let i = 1; i <= pages; i++) {
        items.push(
            <Pagination.Item
                key={i}
                active={i === currentPage}
                onClick={() => handleSelectPage(i)}
            >
                {i}
            </Pagination.Item>
        );
    }

    return (
        <Pagination>
            <Pagination.Prev onClick={() => handlePrevPage()} />
            {items}
            <Pagination.Next onClick={() => handleNextPage()} />
        </Pagination>
    );
};
